import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useTypedSelector } from "../../../../hooks/useTypedSelector";
import useUrlQuery from "../../../../hooks/useUrlQuery";
import { useGetQuote } from "../../../../services/Quotes.service";
import { Close } from "@mui/icons-material";
import {
  setPlansForCompare,
  updatePlansForCompare,
} from "../../../../modules/quotes.slice";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

const SelectedCompare = () => {
  const { selected_plan_for_compare_product_ids } = useTypedSelector(
    (state) => state.quote
  );
  const dispatch = useDispatch();
  const { getUrlQuery } = useUrlQuery();
  const quote_id = getUrlQuery("quote_id");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!selected_plan_for_compare_product_ids.length) return <></>;

  return (
    <>
      <Box
        display="flex"
        position={"fixed"}
        bottom={0}
        height={isMobile ? "auto" : "100px"}
        minHeight={isMobile ? "120px" : "100px"}
        p={isMobile ? "10px" : "20px"}
        backgroundColor="#f1f2f6"
        left={0}
        width={"100%"}
        boxShadow={"0 4px 8px 0 rgba(0,0,0,.4)"}
        borderTop={"1px solid #e3e4e8"}
        zIndex={1000}
      >
        <Grid container spacing={isMobile ? 1 : 2}>
          {/* Product selection area */}
          <Grid item xs={12} md={9}>
            <Grid container spacing={1}>
              {[0, 1, 2].map((item) => (
                <Grid
                  item
                  xs={isSmallMobile ? 12 : 4}
                  sm={4}
                  md={4}
                  key={item}
                >
                  <SelectedProduct
                    selected_plan={selected_plan_for_compare_product_ids[item]}
                    isMobile={isMobile}
                    isSmallMobile={isSmallMobile}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Action buttons area */}
          <Grid item xs={12} md={3}>
            <Box
              width={"100%"}
              justifyContent={isMobile ? "center" : "space-evenly"}
              display={"flex"}
              height={"100%"}
              alignItems={"center"}
              gap={1}
              flexDirection={isMobile ? "row" : "column"}
              mt={isMobile ? 1 : 0}
            >
              <Button
                color="primary"
                variant="contained"
                size={isMobile ? "small" : "medium"}
                fullWidth={isMobile}
                onClick={() => {
                  navigate(`/compare?quote_id=${quote_id}`);
                }}
                sx={{
                  minWidth: isMobile ? "120px" : "auto",
                }}
              >
                Compare
              </Button>
              <Button
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                fullWidth={isMobile}
                onClick={() => {
                  dispatch(setPlansForCompare([]));
                }}
                sx={{
                  minWidth: isMobile ? "120px" : "auto",
                }}
              >
                Cancel
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default SelectedCompare;

const SelectedProduct = ({ selected_plan, isMobile, isSmallMobile }) => {
  const { getUrlQuery } = useUrlQuery();
  const quote_id = getUrlQuery("quote_id");
  const endpoint = selected_plan
    ? `/api/GetHealthQuotes/${selected_plan?.slug}/${selected_plan?.product_id}/${quote_id}/${selected_plan?.cover_amount}`
    : "";
  const { premium_data, product_data } = useGetQuote(endpoint);
  const dispatch = useDispatch();

  return (
    <Box
      width={"100%"}
      height={isSmallMobile ? "60px" : "72px"}
      display={"flex"}
      sx={{
        border: "1px solid #ccc",
        p: isSmallMobile ? 0.5 : 1,
        borderRadius: "8px",
        gap: isSmallMobile ? "4px" : "8px",
        position: "relative",
        minHeight: isSmallMobile ? "60px" : "72px",
      }}
    >
      {selected_plan ? (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: isSmallMobile ? "40px" : "60px",
              maxWidth: isSmallMobile ? "40px" : "80px",
            }}
          >
            <img
              width={isSmallMobile ? "40px" : "60px"}
              height={"auto"}
              src={product_data?.LOGO_PATH}
              alt="compare"
              style={{
                objectFit: "contain",
                maxHeight: isSmallMobile ? "30px" : "40px",
              }}
            />
          </Box>
          <Stack
            sx={{
              flex: 1,
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <Typography
              color="#111"
              fontSize={isSmallMobile ? "11px" : "14px"}
              sx={{
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: isSmallMobile ? "nowrap" : "normal",
                display: "-webkit-box",
                WebkitLineClamp: isSmallMobile ? 1 : 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {product_data?.PRODUCT_NAME}
            </Typography>
            <Typography
              color="text.primary"
              fontSize={isSmallMobile ? "10px" : "12px"}
              sx={{
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {premium_data?.COVER_AMOUNT}
            </Typography>
          </Stack>
          <IconButton
            onClick={() => {
              dispatch(updatePlansForCompare(selected_plan));
            }}
            size="small"
            sx={{
              position: "absolute",
              right: isSmallMobile ? "-8px" : "-12px",
              top: isSmallMobile ? "-8px" : "-12px",
              background: "white",
              width: isSmallMobile ? "20px" : "24px",
              height: isSmallMobile ? "20px" : "24px",
              "&:hover": {
                background: "white",
              },
            }}
          >
            <Close fontSize={isSmallMobile ? "inherit" : "small"} />
          </IconButton>
        </>
      ) : (
        <>
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            width={"100%"}
          >
            <Typography
              fontSize={isSmallMobile ? "12px" : "14px"}
              color="text.secondary"
            >
              Select Plan
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};